import { cloneDeep, get, omit, set, unset } from 'lodash';
import moment from 'moment';

import { t } from '../utils/translations';

export const CaseActions = {
  initInCase(props) {
    const { actions } = this.state;
    const caseProp = actions.case.getCaseProp(props.name);
    if (!props.name) return;

    const configByNamespace = `${props.name}${props.configBy ? `.${props.configBy}` : ``}`;

    const initValue =
      caseProp.value ??
      props.value ??
      actions.case.getLatestCase(props.name) ??
      actions.settings.getUserConfigProp(`widgetsConfig.${configByNamespace}.defaultValue`) ??
      undefined;
    const initValidation =
      initValue !== undefined && initValue !== ''
        ? this.state.actions.caseValidation.validate(props.name, initValue, props.configBy)
        : { errorMessage: '', validatingStatus: 'neutral' };
    const step = actions.userState.getCurrentStep() || 0;

    set(this.state.userData, props.name, {
      ...get(this.state.userData, props.name),
      value: initValue,
      ...initValidation,
      step,
      configBy: props.configBy,
    });
  },

  /**
   * reset widget case entry, validation will still be applied in nextStep()
   * @param {string|string[]} names - widget's name, or an array of names
   */
  resetInCase(names) {
    const { actions } = this.state;

    this.setState((prevState) => {
      if (typeof names === 'string') {
        names = [names];
      }

      const clonedUserData = cloneDeep(prevState.userData);

      names.forEach((name) => {
        set(clonedUserData, name, {
          value: '',
          validatingStatus: 'neutral',
          errorMessage: '',
          step: isNaN(actions.userState.getCurrentStep()) ? undefined : actions.userState.getCurrentStep(),
          configBy: actions.case.getCaseProp(name).configBy,
        });
      });

      return { ...prevState, userData: clonedUserData };
    });
  },

  resetCase() {
    this.setState((prevState) => ({ ...prevState, userData: {} }));
  },

  /**
   * completely remove widget entry from case, validation will not be applied
   * @param {string|string[]} names - widget's name, or an array of names
   */
  removeFromCase(names) {
    if (typeof names === 'string') {
      names = [names];
    }

    this.setState((state) => {
      const clonedUserData = omit(state.userData, names);
      return { ...state, userData: clonedUserData };
    });
  },

  /**
   * update entry in case with option to validate
   *
   * @param {string |[{name, value, validate}]} names - widget's name, or an array of [{name, value, property}]
   * @param {*} [value] - widget's value
   * @param {boolean} [validate] - whether or not should call validate on updated property
   * @param {string} [configBy] - refer to sub configuration
   *
   * @description please use the `prop1.prop2.prop3` format for nested properties
   */

  updateInCase(names, value, validate = true, configBy) {
    if (!names) return;
    const { actions } = this.state;
    if (typeof names === 'string') {
      names = [{ name: names, value, validate, configBy }];
    }

    if (!Array.isArray(names)) return;

    const list = names;
    this.setState((state) => {
      const clonedUserData = cloneDeep(state.userData);
      for (let item of list) {
        // validate prop should be "true" by default
        const validation =
          item.hasOwnProperty('validate') && item.validate === false
            ? {
                errorMessage: '',
                validatingStatus: 'neutral',
              }
            : this.state.actions.caseValidation.validate(item.name, item.value, item.configBy);

        set(clonedUserData, item.name, {
          ...get(state.userData, item.name),
          value: item.value,
          ...validation,
          step: actions.userState.getCurrentStep() || 0,
        });
      }

      return { ...state, userData: clonedUserData };
    });
  },

  getCaseProp(name) {
    return get(this.state.userData, name, {});
  },

  /**
   * get value from case by name
   * @param {string} name - widget's name
   * @param {boolean=false} checkStep - if true, return value only if property shares the same step value as getCurrentStep()
   * @returns {* | undefined} found value or undefined
   */

  getCaseValue(name, fallbackValue = '') {
    const value = get(this.state.userData, `${name}.value`);
    const serverCaseValue = this.state.actions.case.getLatestCase(name);
    if (value !== undefined) return value;
    if (serverCaseValue !== undefined) return serverCaseValue;

    return fallbackValue;
  },

  setPropsToCaseMap(props, includeCaseMapProps) {
    if (Array.isArray(includeCaseMapProps)) {
      includeCaseMapProps.forEach((prop) => {
        const propValue = props[prop];
        if (!propValue) return;

        const caseMapKey = typeof props.toCase === 'string' && prop === 'name' ? props.toCase : propValue;
        set(this.state.config, `caseMap.${caseMapKey}`, propValue);
      });
    }
  },

  removeCaseMapFromProps(props, includeCaseMapProps) {
    if (Array.isArray(includeCaseMapProps)) {
      includeCaseMapProps.forEach((prop) => {
        const propValue = props[prop];
        const caseMapKey = typeof props.toCase === 'string' && prop === 'name' ? props.toCase : propValue;
        unset(this.state.config, `caseMap.${caseMapKey}`);
      });
    }
  },

  setCaseMapKey(key, name) {
    set(this.state.config, `caseMap.${key}`, name || key);
  },

  removeCaseMapKey(key) {
    this.setState(
      (prevState) => {
        let caseMapCloned = cloneDeep(prevState.config.caseMap);
        // console.log('removeCaseMapKey before', key, caseMapCloned);
        caseMapCloned = omit(caseMapCloned, key);
        // console.log('caseMapCloned', caseMapCloned);
        return {
          ...prevState,
          config: {
            ...prevState.config,
            caseMap: caseMapCloned,
          },
        };
      },
      // () => console.log('removeCaseMapKey after', key, this.state.config.caseMap)
    );
  },

  async decision(decisionPolicy) {
    const { actions, sdkActions } = this.state;
    const { minimumRequirements, minimumMarketPlaceItems } =
      actions.settings.getUserConfigProp('widgetsConfig').decision;
    decisionPolicy = decisionPolicy || actions.settings.getUserConfigProp('widgetsConfig.decision.decisionPolicy');

    let widgetValuesCount = 0;
    let widgetsNotInMinimum = {};
    for (let item of minimumRequirements) {
      const widgetValue = actions.case.getCaseValue(item.widgetName);

      widgetValuesCount = widgetValuesCount + widgetValue.length;
      if (typeof widgetValue === 'object' && widgetValue.length < item.minimum) {
        widgetsNotInMinimum[item.widgetName] = item.widgetName;
        actions.view.setAlerts([
          {
            message: t(`decision.invalidMarketplaceItems`),
            type: 'error',
          },
        ]);
        return false;
      }
    }

    if (minimumMarketPlaceItems && widgetValuesCount < minimumMarketPlaceItems) {
      actions.view.setAlerts([{ message: t(`decision.invalidMarketplaceItems`), type: 'error' }]);
      return Promise.reject('invalidMarketplaceItems');
    }

    actions.view.togglePopupLoader(true);

    try {
      await sdkActions.case.update(true);
      const waitTime = actions.settings.getUserConfigProp('widgetsConfig.decision.waitTime') || { seconds: 10 };
      const waitTimeMS = moment.duration(waitTime).asMilliseconds();

      const getQuoteResults = await sdkActions.case.getQuote(false, waitTimeMS, undefined, undefined, decisionPolicy);
      return Promise.resolve(getQuoteResults);
    } catch (e) {
      console.error('decision', e);
      return Promise.reject(e);
    }
  },

  minimumRequirements(name) {
    const { actions } = this.state;
    const { minimumRequirements, minimumMarketPlaceItems } = actions.settings.getUserConfigProp(
      `widgetsConfig.${name}`,
    );

    let widgetValuesCount = 0;
    let widgetsNotInMinimum = {};
    for (let item of minimumRequirements) {
      const widgetValue = actions.case.getCaseValue(item.widgetName);

      widgetValuesCount = widgetValuesCount + widgetValue.length;
      if (typeof widgetValue === 'object' && widgetValue.length < item.minimum) {
        widgetsNotInMinimum[item.widgetName] = item.widgetName;
        actions.view.setAlerts([
          {
            message: t(`${name}.invalidMarketplaceItems`),
            type: 'error',
          },
        ]);
        return false;
      }
    }

    return !(minimumMarketPlaceItems && widgetValuesCount < minimumMarketPlaceItems);
  },

  async decideNextStep(wasStepCompleted = true, props) {
    const { actions, sdkActions } = this.state;

    if (props.toDecision) {
      actions.events.emit('Next_toDecision', { widgetName: `Next` });
      const ez1Up = actions.settings.getUserConfigProp('ez1Up');
      try {
        const decisionResults = await actions.case.decision(props.decisionPolicy);
        if (!ez1Up) return Promise.resolve('ez1Down');
        if (!decisionResults) return Promise.resolve(false);

        if (decisionResults.data.decision === 'UNAVAILABLE') {
          //If broker, redirect him anyway
          const brokerUuid = actions.case.getCaseValue('applicant.brokerUuid');
          if (actions.userState.getUserState('authorities').includes('ROLE_BROKER') && !brokerUuid) {
            sdkActions.users.redirectToDashboard(true);
          } else {
            return Promise.resolve('ez1Down');
          }
        } else {
          sdkActions.users.redirectToDashboard(true);
        }
        return Promise.resolve(decisionResults);
      } catch (e) {
        console.error('decisionResults error', e);
        if (e !== 'invalidMarketplaceItems') {
          sdkActions.users.redirectToDashboard(true);
        }
        return Promise.reject(e);
      }
    } else {
      actions.case.nextStep(wasStepCompleted, props);
      return Promise.resolve(true);
    }
  },

  async moveToStep(shouldMoveToStep) {
    const { actions, sdkActions } = this.state;
    if (typeof shouldMoveToStep === 'function') {
      const step = await shouldMoveToStep();

      try {
        switch (step) {
          case true:
            actions.userState.setCurrentStep(actions.userState.getCurrentStep() + 1);
            break;

          case false:
            await sdkActions.case.updateCaseStep(actions.userState.getCurrentStepName());
            break;

          default:
            await sdkActions.case.updateCaseStep(step);
            actions.userState.setCurrentStepByName(step);
            break;
        }
      } catch (e) {
        console.error('moveToStep', e);
      }
    } else {
      actions.userState.setCurrentStep(actions.userState.getCurrentStep() + 1);
    }
    actions.view.clearAlerts();
    window.scroll(0, 0);
  },

  async nextStep(wasStepCompleted = true, props) {
    const { actions, sdkActions } = this.state;
    actions.view.clearAlerts();

    if (actions.userState.getUserState('demoMode')) {
      await actions.case.moveToStep(props.shouldMoveToStep);
      return;
    }

    try {
      if (props.willValidate && typeof props.willValidate === 'function') {
        const willValidateResults = await props.willValidate();
        if (!willValidateResults) return;
      }

      const invalidProps = actions.caseValidation.validateUserData();
      if (invalidProps) {
        const propsY = [];
        const clonedUserData = cloneDeep(this.state.userData);
        for (let propName in invalidProps) {
          const validation = invalidProps[propName];
          const elementY = actions.view.getElementY(propName);

          elementY && propsY.push({ propName, elementY });
          set(clonedUserData, propName, {
            ...get(clonedUserData, propName),
            ...validation,
          });
        }

        this.setState((state) => {
          return { ...state, userData: clonedUserData };
        });
        if (propsY.length) {
          propsY.sort((a, b) => (a.elementY < b.elementY ? -1 : 1));
          actions.view.scrollToWidget(propsY[0].propName);
        }
      } else {
        actions.view.togglePopupLoader(true);

        let createResults, updateResults;
        const brokerUuid = actions.case.getCaseValue('applicant.brokerUuid');

        if (!actions.userState.getUserState('authorities').includes('ROLE_BROKER') && !brokerUuid) {
          //update user. it should await for result to set new token for next calls
          createResults = await sdkActions.users.create(props.allowLoginOnSignup, props.silentCreateUser);
        }

        if (props.willUpdate && typeof props.willUpdate === 'function') await props.willUpdate();

        let caseData = sdkActions.case.generateCase(wasStepCompleted);
        if (props.overrideCase && typeof props.overrideCase === 'function') {
          caseData = await props.overrideCase(caseData);
        }
        updateResults = await sdkActions.case.update(wasStepCompleted, caseData);

        //Handle move to step
        if (updateResults) {
          await actions.case.moveToStep(props.shouldMoveToStep);
        }
        this.forceUpdate();
      }
      actions.view.togglePopupLoader(false);
    } catch (e) {
      actions.view.togglePopupLoader(false);
      console.error('nextStep', e);
    }
  },

  getLatestCase(path) {
    return path ? get(this.state.latestCase, path) : this.state.latestCase;
  },

  setLatestCase(latestCase) {
    this.setState((prevState) => ({ ...prevState, latestCase }));
  },

  lastStep() {
    const { actions } = this.state;
    actions.view.clearAlerts();
    actions.userState.setCurrentStep(actions.userState.getCurrentStep() - 1);
    window.scroll(0, 0);
  },
};
